/**
 * The dialogs the opportunities/pipeline screens open: the New/Edit
 * Opportunity form, delete confirmation, the stage-change dialog (shared by
 * the list's row menu and the pipeline board's drag-and-drop — closing as
 * lost needs a reason either way), and the not-built explanation dialog.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createRecord } from '../../api/masterData.api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useSelector } from 'react-redux';
import { selectUser } from '../auth/authSlice.js';
import { listContacts } from '../../api/contacts.api.js';
import { listOpportunityProducts } from '../../api/opportunities.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { money } from '../../utils/formatters.js';

const BLANK = {
  name: '', account_id: '', pipeline_id: '', stage_id: '', amount: '', currency: 'USD',
  expected_close_date: '', owner_id: '', next_step: '',
  primary_contact_id: '', source_id: '', probability: '', description: '',
  product_ids: [], competitor_ids: [],
};

function validateOpportunity(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Deal name is required.';
  if (!form.account_id) errors.account_id = 'Choose an account.';
  if (!form.pipeline_id) errors.pipeline_id = 'Choose a pipeline.';
  if (!form.stage_id) errors.stage_id = 'Choose a stage.';
  if (!form.expected_close_date) errors.expected_close_date = 'Set an expected close date.';
  if (form.amount !== '' && Number(form.amount) < 0) errors.amount = 'Amount cannot be negative.';
  if (form.probability !== '' && (Number(form.probability) < 0 || Number(form.probability) > 100)) errors.probability = 'Enter 0 to 100.';
  return errors;
}

/** Create an opportunity, or edit one. `opportunity` (an `opportunityShape.toRow()` row) switches the mode. */
export function OpportunityFormModal({ open, onClose, onSubmit, busy, options, opportunity, defaultPipelineId }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [productsLoaded, setProductsLoaded] = useState(true);
  const editing = Boolean(opportunity);
  const qc = useQueryClient();
  const { can, hasScope } = useAuth();
  const me = useSelector(selectUser);
  /* Reps own what they create; team-or-wider scope (manager, admin) may hand a deal to someone else. */
  const canAssign = hasScope('opportunities', 'team');
  const canAddMaster = can('opportunities', 'create') || can('admin.master', 'create');
  const [quickAdd, setQuickAdd] = useState(null); // 'products' | 'competitors'

  /* Create the catalogue row, refresh that dropdown, then pick the new row on this form. */
  const onQuickAdded = async (entity, row) => {
    await qc.invalidateQueries({ queryKey: queryKeys.masterData.options(entity) });
    const field = entity === 'products' ? 'product_ids' : 'competitor_ids';
    setForm((f) => ({ ...f, [field]: [...f[field], row.id] }));
    setQuickAdd(null);
  };

  /* The contacts of the chosen account — a deal's contact is somebody who works there. */
  const contactsQ = useQuery({
    queryKey: ['opportunity-form', 'contacts', form.account_id],
    queryFn: () => listContacts({ account_id: form.account_id, limit: 100 }),
    enabled: open && Boolean(form.account_id),
    staleTime: 60_000,
  });
  const contactOptions = (contactsQ.data?.data ?? []).map((c) => ({ value: c.id, label: c.full_name ?? c.contact_no }));

  /* An edit needs the lines already on the deal, to show which products are picked
     and to work out what to add or remove on save. */
  const linesQ = useQuery({
    queryKey: queryKeys.opportunities.products(opportunity?.id),
    queryFn: () => listOpportunityProducts(opportunity.id),
    enabled: open && editing,
  });
  const currentLines = linesQ.data?.data ?? [];
  useEffect(() => {
    if (!editing || productsLoaded || !linesQ.data) return;
    setForm((f) => ({ ...f, product_ids: currentLines.filter((l) => l.product).map((l) => l.product.id) }));
    setProductsLoaded(true);
  }, [editing, productsLoaded, linesQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    if (opportunity) {
      setForm({
        name: opportunity.name ?? '',
        account_id: opportunity.account?.id ?? '',
        pipeline_id: opportunity.pipeline?.id ?? '',
        stage_id: opportunity.stage?.id ?? '',
        amount: opportunity.amount ?? '',
        currency: opportunity.currency ?? 'USD',
        expected_close_date: opportunity.expectedCloseDate ?? '',
        owner_id: opportunity.owner?.id ?? '',
        next_step: opportunity.nextStep ?? '',
        primary_contact_id: opportunity.primaryContact?.id ?? '',
        source_id: opportunity.source?.id ?? '',
        // Only a deliberately set probability is shown; otherwise it follows the stage.
        probability: opportunity.probabilityIsOverride && opportunity.probability != null ? String(opportunity.probability) : '',
        description: opportunity.description ?? '',
        product_ids: [],
        competitor_ids: (opportunity.competitors ?? []).map((c) => c.id),
      });
      setProductsLoaded(false);
    } else {
      setForm({ ...BLANK, pipeline_id: defaultPipelineId ?? '' });
    }
    setErrors({});
  }, [open, opportunity, defaultPipelineId]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({
      ...f,
      [k]: value,
      ...(k === 'pipeline_id' ? { stage_id: '' } : {}),
      // A contact belongs to one account, so changing the account clears it.
      ...(k === 'account_id' ? { primary_contact_id: '' } : {}),
    }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const stagesForPipeline = useMemo(
    () => options.stages.filter((s) => s.pipelineId === form.pipeline_id).sort((a, b) => a.sortOrder - b.sortOrder),
    [options.stages, form.pipeline_id],
  );

  const stageProbability = stagesForPipeline.find((st) => st.value === form.stage_id)?.probability ?? null;

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validateOpportunity(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (editing) {
      const payload = {};
      for (const k of ['name', 'account_id', 'amount', 'currency', 'expected_close_date', 'owner_id', 'next_step']) {
        if (form[k] === '') continue;
        payload[k] = k === 'amount' ? Number(form[k]) : form[k];
      }
      // These can be cleared, so an empty box is sent as null rather than skipped.
      payload.primary_contact_id = form.primary_contact_id || null;
      payload.source_id = form.source_id || null;
      payload.description = form.description.trim() || null;
      payload.probability = form.probability === '' ? null : Number(form.probability);
      onSubmit(payload, {
        productIds: form.product_ids,
        competitorIds: form.competitor_ids,
        currentLines,
        currentCompetitorIds: (opportunity.competitors ?? []).map((c) => c.id),
      });
    } else {
      onSubmit({
        name: form.name,
        account_id: form.account_id,
        pipeline_id: form.pipeline_id,
        stage_id: form.stage_id,
        amount: form.amount === '' ? undefined : Number(form.amount),
        currency: form.currency,
        expected_close_date: form.expected_close_date,
        owner_id: canAssign ? (form.owner_id || undefined) : undefined,
        next_step: form.next_step || undefined,
        primary_contact_id: form.primary_contact_id || undefined,
        source_id: form.source_id || undefined,
        probability: form.probability === '' ? undefined : Number(form.probability),
        description: form.description.trim() || undefined,
        products: form.product_ids.length
          ? form.product_ids.map((pid) => ({
            product_id: pid, quantity: 1, unit_price: options.products.find((o) => o.value === pid)?.unitPrice ?? 0,
          }))
          : undefined,
        competitors: form.competitor_ids.length
          ? form.competitor_ids.map((cid, i) => ({ competitor_id: cid, is_primary: i === 0 }))
          : undefined,
      });
    }
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Opportunity' : 'New Opportunity'}
      size="lg"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="oppForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save opportunity'}
          </button>
        </>
      )}
    >
      <form id="oppForm" onSubmit={submit} noValidate>
        <div className="eyebrow mb-2">Opportunity</div>
        <div className="row g-3 mb-3">
          <div className="col-md-8">
            <Field label="Opportunity name" required error={errors.name}>
              <input className="form-control" value={form.name} onChange={set('name')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Sales owner">
              {canAssign ? (
                <SearchSelect
                  value={form.owner_id} onChange={(v) => setForm((f) => ({ ...f, owner_id: v }))}
                  options={options.owners} blank="Me (default)" loading={options.loading}
                />
              ) : (
                <input
                  className="form-control" readOnly disabled
                  value={editing ? (opportunity.owner?.full_name ?? '') : (me?.full_name ?? me?.name ?? 'Me')}
                />
              )}
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Account" required error={errors.account_id}>
              <SearchSelect
                value={form.account_id} onChange={(v) => set('account_id')({ target: { value: v } })}
                options={options.accounts} blank="Search an account…" loading={options.loading}
              />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Contact" help={form.account_id ? undefined : 'Choose an account first.'}>
              <Picker
                value={form.primary_contact_id} onChange={set('primary_contact_id')} options={contactOptions}
                blank={form.account_id ? (contactsQ.isLoading ? 'Loading…' : 'No contact') : 'Choose an account first'}
                disabled={!form.account_id}
              />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Product / Service &amp; Competition</div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="Product / Service" help="Added as one line each; set quantity and price on the deal.">
              <MultiPicker
                value={form.product_ids} options={options.products} loading={options.loading}
                onChange={(ids) => setForm((f) => ({ ...f, product_ids: ids }))}
                blank="Add a product or service…"
              />
              {canAddMaster && (
                <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setQuickAdd('products')}>
                  <i className="bi bi-plus-circle me-1" />Add new product / service
                </button>
              )}
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Competitor">
              <MultiPicker
                value={form.competitor_ids} options={options.competitors} loading={options.loading}
                onChange={(ids) => setForm((f) => ({ ...f, competitor_ids: ids }))}
                blank="Add a competitor…"
              />
              {canAddMaster && (
                <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setQuickAdd('competitors')}>
                  <i className="bi bi-plus-circle me-1" />Add new competitor
                </button>
              )}
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Source">
              <Picker value={form.source_id} onChange={set('source_id')} options={options.sources} blank="Not set" loading={options.loading} />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Deal</div>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <Field label="Pipeline" required error={errors.pipeline_id}>
              <Picker
                value={form.pipeline_id} onChange={set('pipeline_id')} options={options.pipelines}
                blank="Choose a pipeline…" disabled={editing} loading={options.loading}
              />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Sales stage" required error={errors.stage_id}>
              <Picker
                value={form.stage_id} onChange={set('stage_id')} options={stagesForPipeline}
                blank={!options.loading && form.pipeline_id ? 'Choose a stage…' : 'Choose a pipeline first'}
                disabled={editing || !form.pipeline_id} loading={options.loading}
              />
            </Field>
            {editing && <div className="t-2xs ink-4 mt-1">Use the stage menu on the deal to move it.</div>}
          </div>
          <div className="col-md-3">
            <Field label="Estimated amount" error={errors.amount}>
              <input className="form-control" type="number" min="0" step="1000" value={form.amount} onChange={set('amount')} />
            </Field>
          </div>
          <div className="col-md-3">
            <Field label="Currency">
              <input className="form-control" maxLength={3} value={form.currency} onChange={(e) => set('currency')({ target: { value: e.target.value.toUpperCase() } })} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field
              label="Probability (%)" error={errors.probability}
              help={stageProbability != null ? `Blank = the stage's ${stageProbability}%.` : 'Blank = the stage default.'}
            >
              <input className="form-control" type="number" min="0" max="100" step="5" value={form.probability} onChange={set('probability')} placeholder={stageProbability != null ? String(stageProbability) : ''} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Expected closing date" required error={errors.expected_close_date}>
              <input className="form-control" type="date" value={form.expected_close_date} onChange={set('expected_close_date')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Next step" help="What has to happen next.">
              <input className="form-control" value={form.next_step} onChange={set('next_step')} />
            </Field>
          </div>
        </div>

        <div className="eyebrow mb-2">Description</div>
        <Field>
          <textarea className="form-control" rows={3} value={form.description} onChange={set('description')} placeholder="What is the deal about, who is involved, anything the next person should know…" />
        </Field>
      </form>
    </Modal>
    <QuickAddModal entity={quickAdd} onClose={() => setQuickAdd(null)} onAdded={onQuickAdded} />
    </>
  );
}

/**
 * Options come from `useOpportunityOptions()`'s master-data queries, which can
 * still be in flight when a fast click opens this modal. Without `loading`,
 * an empty list and a genuinely-empty one render identically — "not
 * populated" is indistinguishable from "still fetching" — so a still-loading
 * picker says so instead of showing a bare, indistinguishable blank option.
 */
/**
 * Pick several values: chips for what is chosen, one select to add another.
 * A plain `<select multiple>` needs Ctrl/Cmd-click, which nobody finds unaided.
 */
function MultiPicker({ value, options, onChange, blank, loading = false }) {
  const label = (id) => options.find((o) => o.value === id)?.label ?? '…';
  const remaining = options.filter((o) => !value.includes(o.value));
  return (
    <div>
      {value.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {value.map((id) => (
            <span key={id} className="pill pill-violet d-inline-flex align-items-center gap-1">
              {label(id)}
              <button
                type="button" className="btn-close btn-sm" style={{ fontSize: 8 }}
                aria-label={`Remove ${label(id)}`} onClick={() => onChange(value.filter((v) => v !== id))}
              />
            </span>
          ))}
        </div>
      )}
      <SearchSelect
        value="" onChange={(v) => { if (v) onChange([...value, v]); }} options={remaining} loading={loading}
        blank={remaining.length ? blank : 'Nothing left to add'} disabled={!remaining.length}
      />
    </div>
  );
}

/** Single-value combobox with type-to-filter. `value` is an option value; '' = none. */
function SearchSelect({ value, onChange, options, blank = 'Select…', disabled = false, loading = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const selected = options.find((o) => o.value === value);
  const shown = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 100);
  const pick = (v) => { onChange(v); setOpen(false); setQ(''); };
  const busy = loading && options.length === 0;
  return (
    <div ref={ref} className="position-relative">
      <input
        className="form-control" disabled={disabled || busy} autoComplete="off"
        placeholder={busy ? 'Loading…' : (selected?.label ?? blank)}
        value={open ? q : (selected?.label ?? '')}
        onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); setQ(''); }
          if (e.key === 'Enter' && open) { e.preventDefault(); if (shown[0]) pick(shown[0].value); }
        }}
      />
      {open && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 20, maxHeight: 220, overflowY: 'auto', top: '100%' }} role="listbox"
        >
          {value && <li className="list-group-item list-group-item-action text-muted" role="option" onMouseDown={() => pick('')}>{blank}</li>}
          {shown.map((o) => (
            <li
              key={o.value} role="option" aria-selected={o.value === value}
              className={`list-group-item list-group-item-action${o.value === value ? ' active' : ''}`}
              style={{ cursor: 'pointer' }} onMouseDown={() => pick(o.value)}
            >
              {o.label}
            </li>
          ))}
          {!shown.length && <li className="list-group-item text-muted">No matches</li>}
        </ul>
      )}
    </div>
  );
}

const Picker = ({ value, onChange, options, blank = 'Not set', disabled = false, loading = false }) => (
  <select className="form-select" value={value} onChange={onChange} disabled={disabled || (loading && options.length === 0)}>
    <option value="">{loading && options.length === 0 ? 'Loading…' : blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/** Delete, with the reason the database insists on. */
export function DeleteOpportunityModal({ open, opportunity, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete opportunity"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x"
            disabled={!reason.trim() || busy} onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Deleting <strong>{opportunity?.name}</strong> ({opportunity?.opportunityNo}).
        The row stays in the database with <code>deleted_at</code> set.
      </p>
      <Field label="Reason" required help="Written to the audit trail.">
        <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate of OP-00090" autoFocus />
      </Field>
    </Modal>
  );
}

/**
 * Move a deal to another stage. Closing as lost (the target stage's
 * `isClosed && !isWon`) switches the form to require a reason — the same
 * `422 lost_reason_required` the server enforces, asked up front instead of
 * surfacing as a rejected drop.
 */
export function StageChangeModal({ open, opportunity, targetStage, onClose, onConfirm, busy, options }) {
  const [lostReasonId, setLostReasonId] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [lostCompetitorId, setLostCompetitorId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) { setLostReasonId(''); setLostNote(''); setLostCompetitorId(''); setNote(''); }
  }, [open, targetStage?.value]);

  const closingLost = Boolean(targetStage?.isClosed) && !targetStage?.isWon;
  const closingWon = Boolean(targetStage?.isClosed) && Boolean(targetStage?.isWon);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={closingLost ? 'Close as lost' : closingWon ? 'Close as won' : `Move to ${targetStage?.label ?? '…'}`}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-brand btn-sm-x"
            style={closingLost ? { background: 'var(--rose)', borderColor: 'transparent' } : undefined}
            disabled={busy || (closingLost && !lostReasonId)}
            onClick={() => onConfirm({
              stage_id: targetStage.value,
              note: note || undefined,
              ...(closingLost ? { lost_reason_id: lostReasonId, lost_note: lostNote || undefined, lost_to_competitor_id: lostCompetitorId || undefined } : {}),
            })}
          >
            {busy ? <><ButtonSpinner />Moving…</> : closingLost ? 'Close as lost' : closingWon ? 'Close as won' : 'Move'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Moving <strong>{opportunity?.name}</strong> ({money(opportunity?.amount, opportunity?.currency)}) to{' '}
        <strong>{targetStage?.label}</strong>.
      </p>

      {targetStage?.exitCriteria && !closingLost && !closingWon && (
        <div className="t-xs p-2 px-3 mb-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--line-2)', borderRadius: 'var(--r)' }}>
          <i className="bi bi-info-circle me-1" aria-hidden="true" />Exit criteria for this stage: {targetStage.exitCriteria}
        </div>
      )}

      {closingLost && (
        <>
          <Field label="Lost reason" required help="The database rejects the move without one — loss analysis depends on it.">
            <select className="form-select" value={lostReasonId} onChange={(e) => setLostReasonId(e.target.value)}>
              <option value="">Choose a reason…</option>
              {options.lostReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <div className="row g-3 mt-1">
            <div className="col-md-6">
              <Field label="Lost to (competitor)">
                <select className="form-select" value={lostCompetitorId} onChange={(e) => setLostCompetitorId(e.target.value)}>
                  <option value="">Not applicable</option>
                  {options.competitors.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Note">
                <input className="form-control" value={lostNote} onChange={(e) => setLostNote(e.target.value)} placeholder="What happened" />
              </Field>
            </div>
          </div>
        </>
      )}

      {!closingLost && (
        <Field label="Note" help="Recorded on the stage-history entry.">
          <input className="form-control" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" autoFocus />
        </Field>
      )}
    </Modal>
  );
}

/** Why a header action is disabled, in a dialog rather than a tooltip — same pattern as leads/accounts/contacts. */
export function NotBuiltModal({ open, action, onClose }) {
  if (!action) return null;
  return (
    <Modal
      open={open} onClose={onClose} title={action.title}
      footer={<button type="button" className="btn btn-brand btn-sm-x" onClick={onClose}>Close</button>}
    >
      <p className="t-sm ink-2">{action.why}</p>
      <div className="t-xs ink-3">Endpoint: <code>{action.endpoint}</code> — {action.section}</div>
    </Modal>
  );
}

const QUICK = {
  products: { title: 'Add product / service', blank: { name: '', sku: '', unit_price: '', currency: 'USD' } },
  competitors: { title: 'Add competitor', blank: { name: '', website: '' } },
};

/** Small create form for a catalogue row, opened from the opportunity form. */
function QuickAddModal({ entity, onClose, onAdded }) {
  const [v, setV] = useState({});
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => { if (entity) { setV(QUICK[entity].blank); setErr({}); setMsg(''); } }, [entity]);
  if (!entity) return null;
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const errs = {};
    if (!v.name?.trim()) errs.name = 'Required';
    if (entity === 'products') {
      if (!v.sku?.trim()) errs.sku = 'Required';
      if (!/^[A-Za-z]{3}$/.test(v.currency ?? '')) errs.currency = '3-letter code';
      if (v.unit_price !== '' && !(Number(v.unit_price) >= 0)) errs.unit_price = 'Invalid';
    }
    setErr(errs);
    if (Object.keys(errs).length) return;
    const payload = entity === 'products'
      ? { name: v.name.trim(), sku: v.sku.trim(), currency: v.currency.toUpperCase(), unit_price: Number(v.unit_price || 0), is_active: true }
      : { name: v.name.trim(), ...(v.website?.trim() ? { website: v.website.trim() } : {}), is_active: true };
    setBusy(true);
    setMsg('');
    try {
      const row = await createRecord(entity, payload);
      await onAdded(entity, row);
    } catch (ex) {
      setMsg(ex?.response?.data?.error?.message ?? ex?.message ?? 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open onClose={onClose} title={QUICK[entity].title}
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="quickAddForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : 'Add'}
          </button>
        </>
      )}
    >
      <form id="quickAddForm" onSubmit={submit} noValidate>
        {msg && <div className="alert alert-danger py-2">{msg}</div>}
        <div className="row g-3">
          <div className={entity === 'products' ? 'col-md-7' : 'col-12'}>
            <Field label={entity === 'products' ? 'Product / service name' : 'Competitor name'} required error={err.name}>
              <input className="form-control" value={v.name ?? ''} onChange={set('name')} maxLength={200} autoFocus />
            </Field>
          </div>
          {entity === 'products' ? (
            <>
              <div className="col-md-5">
                <Field label="SKU" required error={err.sku}>
                  <input className="form-control" value={v.sku ?? ''} onChange={set('sku')} maxLength={60} />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="List price" error={err.unit_price}>
                  <input className="form-control" type="number" min="0" step="any" value={v.unit_price ?? ''} onChange={set('unit_price')} />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Currency" required error={err.currency}>
                  <input className="form-control" value={v.currency ?? ''} onChange={set('currency')} maxLength={3} />
                </Field>
              </div>
            </>
          ) : (
            <div className="col-12">
              <Field label="Website">
                <input className="form-control" value={v.website ?? ''} onChange={set('website')} maxLength={500} />
              </Field>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
